import { type TypographyProps } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";

import {
  DEFAULT_BUTTON_COLLAPSE_TEXT,
  DEFAULT_BUTTON_EXPAND_TEXT,
  DEFAULT_MAX_COLLAPSED_LINE,
} from "~/components/UI/CollapsedText/constants";
import { getCollapsedMaxHeightPx } from "~/components/UI/CollapsedText/getCollapsedMaxHeightPx";

import * as Styled from "./CollapsedText.styles";

export interface CollapsedTextProps extends TypographyProps {
  maxCollapsedLine?: number;
  maxCollapsedHeightPx?: number;
  buttonCollapseText?: string;
  buttonExpandText?: string;
  sectionIdToExpand?: string;
}

export const CollapsedText: FC<CollapsedTextProps> = ({
  children,
  variant = "caption",
  maxCollapsedLine = DEFAULT_MAX_COLLAPSED_LINE,
  maxCollapsedHeightPx,
  buttonCollapseText = DEFAULT_BUTTON_COLLAPSE_TEXT,
  buttonExpandText = DEFAULT_BUTTON_EXPAND_TEXT,
  sectionIdToExpand,
  ...typographyProps
}) => {
  const theme = useTheme();
  const { hash } = useLocation();
  const [expanded, setExpanded] = useState(
    !!sectionIdToExpand && hash === `#${sectionIdToExpand}`
  );
  const [hasOverflow, setHasOverflow] = useState(false);
  const textRef = useRef<HTMLElement | null>(null);

  const collapsedHeightPx = useMemo(
    () =>
      maxCollapsedHeightPx ??
      getCollapsedMaxHeightPx(theme, variant, maxCollapsedLine),
    [maxCollapsedHeightPx, maxCollapsedLine, theme, variant]
  );

  useEffect(() => {
    if (!sectionIdToExpand || hash !== `#${sectionIdToExpand}`) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setExpanded(true);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [hash, sectionIdToExpand]);

  useEffect(() => {
    if (expanded) {
      return;
    }

    const node = textRef.current;

    if (!node) {
      setHasOverflow(false);
      return;
    }

    const checkOverflow = () => {
      setHasOverflow(node.scrollHeight > collapsedHeightPx + 1);
    };

    const frame = window.requestAnimationFrame(() => {
      checkOverflow();
    });

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(node);

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [children, collapsedHeightPx, expanded]);

  if (children == null || children === "") {
    return null;
  }

  return (
    <Styled.Root spacing={1.5}>
      <Styled.CollapsedTextContent
        ref={(node) => {
          textRef.current = node;
        }}
        variant={variant}
        expanded={expanded}
        maxCollapsedLine={maxCollapsedLine}
        maxCollapsedHeightPx={maxCollapsedHeightPx}
        {...typographyProps}
      >
        {children}
      </Styled.CollapsedTextContent>
      {hasOverflow && (
        <Styled.ExpandButton
          role="button"
          tabIndex={0}
          onClick={() => setExpanded((value) => !value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setExpanded((value) => !value);
            }
          }}
        >
          {expanded ? buttonCollapseText : buttonExpandText}
        </Styled.ExpandButton>
      )}
    </Styled.Root>
  );
};
